import React, { useState, useEffect } from "react";
import apiService from "../../../../services/ApiService";
import TailwindModal from "../../../Tailwind/TailwindModal";
import snakeToPascal from "../../../../tools";

interface DependencyItem {
    field_id: number;
    field_name: string;
    model_data_id: number;
    model_data_name: string;
    kind: string;
}

interface FieldDependenciesData {
    field_id: number;
    field_name: string;
    model_data_id: number;
    model_data_name: string;
    kind: string;
    upstream: DependencyItem[];
    downstream: DependencyItem[];
}

interface FieldDependenciesPanelProps {
    isOpen: boolean;
    onClose: () => void;
    scenarioId: number;
    fieldId: number;
    fieldName: string;
    onNavigate?: (modelDataId: string, kind: string) => void;
}

export default function FieldDependenciesPanel({ isOpen, onClose, scenarioId, fieldId, fieldName, onNavigate }: FieldDependenciesPanelProps) {
    const [data, setData] = useState<FieldDependenciesData | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen && scenarioId && fieldId) {
            setLoading(true);
            apiService.getFieldDependencies(scenarioId, fieldId).then(async (response) => {
                if (response && response.ok) {
                    const json = await response.json();
                    setData(json);
                }
                setLoading(false);
            });
        }
    }, [isOpen, scenarioId, fieldId]);

    const handleNavigate = (item: DependencyItem) => {
        if (onNavigate) {
            onClose();
            onNavigate(item.model_data_id.toString(), item.kind);
        }
    };

    const DependencyList = ({ items, emptyMessage }: { items: DependencyItem[], emptyMessage: string }) => {
        if (items.length === 0) {
            return <p className="text-sm text-slate-400 italic">{emptyMessage}</p>;
        }
        return (
            <div className="space-y-1">
                {items.map((item) => (
                    <button
                        key={item.field_id}
                        onClick={() => handleNavigate(item)}
                        className={`w-full flex items-center gap-2 p-2 rounded-lg border border-slate-100 text-left transition-colors ${onNavigate ? 'hover:bg-slate-100 cursor-pointer' : 'hover:bg-slate-50'}`}
                    >
                        <span className={`material-symbols-outlined text-[16px] ${item.kind === 'input' ? 'text-blue-500' : 'text-emerald-500'}`}>
                            {item.kind === 'input' ? 'input' : 'output'}
                        </span>
                        <div className="flex flex-col">
                            <span className="text-sm font-medium text-slate-700">{snakeToPascal(item.field_name)}</span>
                            <span className="text-xs text-slate-400">{item.model_data_name}</span>
                        </div>
                        <span className={`ml-auto text-[10px] font-medium px-1.5 py-0.5 rounded ${item.kind === 'input' ? 'bg-blue-50 text-blue-600' : 'bg-emerald-50 text-emerald-600'}`}>
                            {item.kind}
                        </span>
                        {onNavigate && (
                            <span className="material-symbols-outlined text-[14px] text-slate-300">chevron_right</span>
                        )}
                    </button>
                ))}
            </div>
        );
    };

    return (
        <TailwindModal isOpen={isOpen} onClose={onClose} title={`Dependencies — ${fieldName}`} size="md">
            <div className="p-4">
                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <span className="material-symbols-outlined animate-spin text-slate-400">progress_activity</span>
                    </div>
                ) : data ? (
                    <div className="space-y-4">
                        <div>
                            <h3 className="text-sm font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
                                <span className="material-symbols-outlined text-[16px]">arrow_upward</span>
                                Depends on ({data.upstream.length})
                            </h3>
                            <DependencyList items={data.upstream} emptyMessage="No upstream dependencies" />
                        </div>
                        <hr className="border-slate-200" />
                        <div>
                            <h3 className="text-sm font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
                                <span className="material-symbols-outlined text-[16px]">arrow_downward</span>
                                Used by ({data.downstream.length})
                            </h3>
                            <DependencyList items={data.downstream} emptyMessage="No downstream dependencies" />
                        </div>
                    </div>
                ) : (
                    <p className="text-sm text-slate-400 text-center py-8">No dependency data available</p>
                )}
            </div>
        </TailwindModal>
    );
}
